import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { RootState, useAppDispatch, useAppSelector } from "../Store/store";
import { removeCurrentUser } from "../Store/Slices/authSlice";
import { ContactLinkResult, ICompanyResponse, IGetMe, IMenuItemOptionResponce, UserRoles } from "../Interfaces/responseTypes";
import { ContactLinkTypes, CurrencyTypes, IAutocompleteData, ICartOption, MeasureTypes, MenuCategoryTypes, TimeUnitTypes } from "../Interfaces/componentTypes";
import { Manager, Menu, MenuDessert, MenuDrinks, MenuFood, MenuOther, Waiter } from "../Assets/Icons";
import useLanguagesHooks from "../Components/Languages/setLanguage-hooks";
import { toast } from "react-toastify";
import CustomToast from "../Components/Toast/toast";
import moment from "moment";
import { useCallback } from "react";


export const useGeneralHooks = () => {
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.language;
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("p_menu_token");
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(
    (state: RootState) => state?.auth?.currentUser
  );
  const getMeError = useAppSelector(
    (state: RootState) => state?.auth?.error
  );
  const location = useLocation();
  const params = useParams();
  const baseURL = process.env.REACT_APP_API_KEY;
  const mobileScreen: boolean = window.innerWidth < 480;
  const smallScreen: boolean = window.innerWidth < 350;
  const mediumScreen: boolean = window.innerWidth < 680;
  const largeScreen: boolean = window.innerWidth > 1010;
  function isUser(user: any): user is IGetMe {
    return user && (user as IGetMe).id !== undefined;
  };
  const onLogout = ({ isExpired = false }: { isExpired?: boolean }) => {
    localStorage.removeItem('p_menu_token');
    dispatch(removeCurrentUser())
    isExpired ? navigate('/login') : navigate('/')
  };

  return {
    t,
    i18n,
    accessToken,
    navigate,
    isUser,
    dispatch,
    onLogout,
    location,
    params,
    baseURL,
    currentUser,
    mobileScreen,
    mediumScreen,
    largeScreen,
    smallScreen,
    getMeError,
    activeLanguage
  }
};

export const useDirectoriesHook = () => {
  const { t } = useTranslation();

  const menuTypeOptions: IAutocompleteData = [
    {
      title: t('Directories.Food'),
      id: MenuCategoryTypes.FOOD,
      icon: MenuFood
    },
    {
      title: t('Directories.Drink'),
      id: MenuCategoryTypes.DRINK,
      icon: MenuDrinks
    },
    {
      title: t('Directories.Dessert'),
      id: MenuCategoryTypes.DESSERT,
      icon: MenuDessert
    },
    {
      title: t('Directories.Other'),
      id: MenuCategoryTypes.OTHER,
      icon: MenuOther
    }
  ];

  const menuItemMeasureTypes: IAutocompleteData = [
    {
      id: MeasureTypes.BIG,
      title: t('Directories.Measure.Big')
    },
    {
      id: MeasureTypes.NORMAL,
      title: t('Directories.Measure.Normal')
    },
    {
      id: MeasureTypes.SMALL,
      title: t('Directories.Measure.Small')
    },
    {
      id: MeasureTypes.UNIT,
      title: t('Directories.Measure.Unit')
    },
    {
      id: MeasureTypes.PIECE,
      title: t('Directories.Measure.Piece')
    },
    {
      id: MeasureTypes.PORTION,
      title: t('Directories.Measure.Portion')
    },
    {
      id: MeasureTypes.KILOGRAM,
      title: t('Directories.Measure.Kg')
    },
    {
      id: MeasureTypes.GRAM,
      title: t('Directories.Measure.Gram')
    },
    {
      id: MeasureTypes.POUND,
      title: t('Directories.Measure.Pound')
    },
    {
      id: MeasureTypes.OUNCE,
      title: t('Directories.Measure.Ounce')
    },
    {
      id: MeasureTypes.CUP,
      title: t('Directories.Measure.Cup')
    },
    {
      id: MeasureTypes.BOTTLE,
      title: t('Directories.Measure.Bottle')
    },
    {
      id: MeasureTypes.LITER,
      title: t('Directories.Measure.Liter')
    },
    {
      id: MeasureTypes.MILLILITER,
      title: t('Directories.Measure.Ml')
    },
  ];

  const timeUnits: IAutocompleteData = [
    {
      id: TimeUnitTypes.HOUR,
      title: t('Directories.Time.Hour')
    },
    {
      id: TimeUnitTypes.MINUTE,
      title: t('Directories.Time.Minute')
    }
  ];

  const currencies: IAutocompleteData = [
    {
      id: CurrencyTypes.AMD,
      title: 'AMD'
    },
    {
      id: CurrencyTypes.RUB,
      title: 'RUB'
    },
    {
      id: CurrencyTypes.USD,
      title: 'USD'
    },
  ];

  const roles: IAutocompleteData = [
    {
      id: UserRoles.MANAGER,
      title: t('Directories.Roles.Manager'),
      icon: Manager
    },
    {
      id: UserRoles.WAITER,
      title: t('Directories.Roles.Waiter'),
      icon: Waiter
    },
  ];

  const rolesFilterValues: IAutocompleteData = [
    {
      id: 'all',
      title: t('My_Business.Personal.All'),
      icon: Menu
    },
    {
      id: UserRoles.MANAGER,
      title: t('Directories.Roles.Manager'),
      icon: Manager
    },
    {
      id: UserRoles.WAITER,
      title: t('Directories.Roles.Waiter'),
      icon: Waiter
    },
  ];



  return {
    roles,
    timeUnits,
    currencies,
    menuTypeOptions,
    rolesFilterValues,
    menuItemMeasureTypes,
  }
};

export const useNoAuthHooks = () => {
  const baseURL = process.env.REACT_APP_API_KEY;
  const navigate = useNavigate();
  const { t } = useLanguagesHooks();
  const mobileScreen: boolean = window.innerWidth < 480;
  const smallScreen: boolean = window.innerWidth < 350;
  const mediumScreen: boolean = window.innerWidth < 680;
  const largeScreen: boolean = window.innerWidth > 1010;

  const renderOptions = (option: IMenuItemOptionResponce | ICartOption): string => {
    const type = option.measure_type;
    switch (type) {
      case MeasureTypes.BIG:
        return t('Render_Options.Big');
      case MeasureTypes.NORMAL:
        return t('Render_Options.Normal');
      case MeasureTypes.SMALL:
        return t('Render_Options.Small');
      case MeasureTypes.UNIT:
        const unitCount = Number(option.measure_value)
        if (unitCount > 1) {
          return `${unitCount} ${t('Render_Options.Units')}`;
        } else {
          return `${unitCount} ${t('Render_Options.Unit')}`;
        };
      case MeasureTypes.PIECE:
        const pieceCount = Number(option.measure_value)
        if (pieceCount > 1) {
          return `${pieceCount} ${t('Render_Options.Pieces')}`;
        } else {
          return `${pieceCount} ${t('Render_Options.Piece')}`;
        };
      case MeasureTypes.PORTION:
        const portionCount = Number(option.measure_value)
        return `${portionCount} ${t('Render_Options.Portion')}`;
      case MeasureTypes.KILOGRAM:
        const kgCount = Number(option.measure_value)
        return `${kgCount} ${t('Render_Options.Kg')}`;
      case MeasureTypes.GRAM:
        const gramCount = Number(option.measure_value)
        return `${gramCount} ${t('Render_Options.Gram')}`;
      case MeasureTypes.POUND:
        const poundCount = Number(option.measure_value)
        return `${poundCount} ${t('Render_Options.Pound')}`;
      case MeasureTypes.OUNCE:
        const ounceCount = Number(option.measure_value)
        return `${ounceCount} ${t('Render_Options.Ounce')}`;
      case MeasureTypes.CUP:
        const cupCount = Number(option.measure_value)
        return `${cupCount} ${t('Render_Options.Cup')}`;
      case MeasureTypes.BOTTLE:
        const bottleCount = Number(option.measure_value)
        return `${bottleCount} ${t('Render_Options.Bottle')}`;
      case MeasureTypes.LITER:
        const literCount = Number(option.measure_value)
        return `${literCount} ${t('Render_Options.Liter')}`;
      case MeasureTypes.MILLILITER:
        const mlCount = Number(option.measure_value)
        return `${mlCount} ${t('Render_Options.Ml')}`;
      default:
        return ''
    }
  };

  return {
    baseURL,
    smallScreen,
    largeScreen,
    mobileScreen,
    mediumScreen,
    renderOptions,
    navigate,
  }
};

export const useToastify = () => {


  const showToastSuccess = (message: string, duration?: number) => {
    toast((props) => (
      <CustomToast {...props} type="success">
        {message}
      </CustomToast>
    ), {
      position: "top-right",
      autoClose: duration ?? 1500,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
    });
  };


  const showToastError = (message: string, duration?: number) => {
    toast((props) => (
      <CustomToast {...props} type="error">
        {message}
      </CustomToast>
    ), {
      position: "top-right",
      autoClose: duration ?? 1500,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
    });
  };

  return {
    showToastSuccess,
    showToastError
  }
};


export const useExprationHook = (company?: ICompanyResponse) => {
  let demoDaysLeft: number | null = null;
  let demoStatus: "active" | "expiringSoon" | "expired" | null = null;

  let paidDaysLeft: number | null = null;
  let paidDaysAfter: number | null = null;
  let paidStatus: "active" | "expiringSoon" | "gracePeriod" | "expired" | null = null;

  if (company) {
    const now = moment();

    if (company.demo_version) {
      const demoExpire = moment(company.demo_expired_at, "DD-MM-YYYY HH:mm");
      demoDaysLeft = demoExpire.diff(now, "days");

      if (demoDaysLeft < 0) demoStatus = "expired";
      else if (demoDaysLeft <= 3) demoStatus = "expiringSoon";
      else demoStatus = "active";
    } else {
      const paidExpire = moment(company.expired_at, "DD-MM-YYYY HH:mm");
      paidDaysLeft = paidExpire.diff(now, "days");
      paidDaysAfter = now.diff(paidExpire, "days");

      if (paidDaysLeft > 3) paidStatus = "active";
      else if (paidDaysLeft <= 3 && paidDaysLeft >= 0) paidStatus = "expiringSoon";
      else if (paidDaysAfter > 0 && paidDaysAfter <= 5) paidStatus = "gracePeriod";
      else if (paidDaysAfter > 5) paidStatus = "expired";
    }
  };

  return {
    demoStatus,
    paidStatus,
    demoDaysLeft,
    paidDaysLeft,
    paidDaysAfter,
  }
}

export const useSmoothScroll = () => {
    const smoothScrollTo = useCallback(
        (element: HTMLElement | null, target: number, duration: number = 500) => {
            if (!element) return;

            const start = element.scrollTop;
            const change = target - start;
            const startTime = performance.now();

            const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
                t /= d / 2;
                if (t < 1) return (c / 2) * t * t + b;
                t--;
                return (-c / 2) * (t * (t - 2) - 1) + b;
            };

            const animateScroll = (currentTime: number) => {
                const timeElapsed = currentTime - startTime;
                const newPos = easeInOutQuad(timeElapsed, start, change, duration);
                element.scrollTop = newPos;

                if (timeElapsed < duration) {
                    requestAnimationFrame(animateScroll);
                } else {
                    element.scrollTop = target; 
                }
            };

            requestAnimationFrame(animateScroll);
        },
        []
    );

    return smoothScrollTo;
};